import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Grid, Paper, TablePagination, InputAdornment,
  Tabs, Tab
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Undo as UndoIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  listWholesaleOrders,
  getWholesaleOrdersSummary,
  markWholesaleOrderInvoiced
} from '../../services/retailOrdersService';
import api from '../../config/api';
import { PageHeader } from '../ds';
import { useUrlState } from '../../hooks/useUrlState';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Inline void-invoice call — kept here (rather than in retailOrdersService.js)
// so this feature stays within the wholesale-orders module boundary.
async function voidWholesaleOrderInvoice(brandId, orderId) {
  const response = await api.post(
    `${API_BASE}/api/wholesale-orders/${brandId}/${orderId}/void-invoice`
  );
  return response.data;
}

// Inline mark-paid call — same boundary reasoning as voidWholesaleOrderInvoice.
async function markWholesaleOrderPaid(brandId, orderId) {
  const response = await api.post(
    `${API_BASE}/api/wholesale-orders/${brandId}/${orderId}/mark-paid`
  );
  return response.data;
}

const SEND_METHOD_LABEL = {
  qb: 'QuickBooks',
  sps_commerce: 'SPS Commerce',
  procuros: 'Procuros',
  thg_procure: 'THG Procure',
  portal: 'Portal',
};

const SEND_METHOD_COLOR = {
  qb: 'primary',
  sps_commerce: 'warning',
  procuros: 'warning',
  thg_procure: 'warning',
  portal: 'info',
};

export default function ReadyToInvoice() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // 'queue' = orders awaiting invoice (qb_invoice_id = ''); 'invoiced' = already
  // invoiced orders (qb_invoice_id != ''). URL-bound so Slack-deeplinks land on
  // the right tab.
  const [tab, setTab] = useUrlState('tab', 'queue');
  // URL-bound filter state so browser-back from CustomerHistory restores it.
  const [activeSubsidiary, setActiveSubsidiary] = useUrlState('sub', 'all');
  const [search, setSearch] = useUrlState('q', '');
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });
  const skipFirstPageReset = useRef(true);

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [qbInvoiceId, setQbInvoiceId] = useState('');
  const [qbInvoiceDate, setQbInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paidTarget, setPaidTarget] = useState(null);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      // The summary card row always reflects the Ready-to-Invoice queue —
      // it's the operational KPI ("how much is waiting to be invoiced") —
      // independent of which tab is active. The orders list switches.
      const ordersParams = tab === 'invoiced'
        ? { invoiced_only: true }
        : { ready_only: true };
      const [ordersResp, summaryResp] = await Promise.all([
        listWholesaleOrders(ordersParams),
        getWholesaleOrdersSummary()
      ]);
      setOrders(ordersResp.data || []);
      setSummary(summaryResp.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build subsidiary_id → name map from summary cards
  const subsidiaryNameMap = useMemo(() => {
    const m = {};
    summary.forEach(g => { m[g.subsidiary_id] = g.subsidiary_name || (g.subsidiary_id === 0 ? 'Unmapped' : `Subsidiary #${g.subsidiary_id}`); });
    return m;
  }, [summary]);

  const filtered = useMemo(() => {
    let rows = orders;
    if (activeSubsidiary !== 'all') {
      const sid = parseInt(activeSubsidiary);
      rows = rows.filter(o => (o.subsidiary_id || 0) === sid);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(o =>
        (o.order_id || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q) ||
        (o.customer_id || '').toLowerCase().includes(q) ||
        (o.po_number || '').toLowerCase().includes(q) ||
        (o.brand_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [orders, activeSubsidiary, search]);

  // Reset page when filters change so user doesn't land on empty page N — but
  // skip the very first run so a hydrated `?p=N` from the URL isn't wiped.
  useEffect(() => {
    if (skipFirstPageReset.current) { skipFirstPageReset.current = false; return; }
    setPage(0);
  }, [activeSubsidiary, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const paged = useMemo(() => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage), [filtered, page, rowsPerPage]);

  const openInvoice = (order) => {
    setInvoiceTarget(order);
    // Auto-suggest invoice number: INV-{YYYYMMDD}-{order_id-last-6}. Operator
    // can still type their own (e.g. QuickBooks ID); this just fills the box
    // so 90% of cases don't need typing.
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const tail = String(order.order_id || '').replace(/^CIN7-/, '').slice(-6) || '0001';
    setQbInvoiceId(`INV-${stamp}-${tail}`);
    setQbInvoiceDate(new Date().toISOString().slice(0, 10));
    setError(null); setInvoiceDialogOpen(true);
  };

  const submitInvoice = async () => {
    if (!qbInvoiceId.trim()) {
      setError('QuickBooks Invoice ID is required');
      return;
    }
    try {
      setSaving(true);
      await markWholesaleOrderInvoiced(invoiceTarget.brand_id, invoiceTarget.order_id, qbInvoiceId.trim(), qbInvoiceDate);
      setSuccess(`Marked ${invoiceTarget.order_id} as invoiced (${qbInvoiceId.trim()})`);
      setInvoiceDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const openVoid = (order) => {
    setVoidTarget(order);
    setError(null); setVoidDialogOpen(true);
  };

  const openMarkPaid = (order) => {
    setPaidTarget(order);
    setError(null); setPaidDialogOpen(true);
  };

  const submitMarkPaid = async () => {
    if (!paidTarget) return;
    try {
      setMarking(true);
      await markWholesaleOrderPaid(paidTarget.brand_id, paidTarget.order_id);
      setSuccess(`Marked ${String(paidTarget.order_id || '').replace(/^CIN7-/, '')} as paid.`);
      setPaidDialogOpen(false);
      setPaidTarget(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setMarking(false);
    }
  };

  const submitVoid = async () => {
    if (!voidTarget) return;
    try {
      setVoiding(true);
      const prevInv = voidTarget.qb_invoice_id;
      await voidWholesaleOrderInvoice(voidTarget.brand_id, voidTarget.order_id);
      setSuccess(`Voided invoice ${prevInv} — order ${String(voidTarget.order_id || '').replace(/^CIN7-/, '')} returned to the Ready-to-Invoice queue.`);
      setVoidDialogOpen(false);
      setVoidTarget(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setVoiding(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  const formatCurrencyDict = (dict) =>
    // DT-1453: the '' bucket (orders with no currency) is a plain number, no leading space.
    Object.entries(dict).map(([c, v]) => `${c} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim()).join(' + ');

  return (
    <Box>
      <PageHeader
        primary={tab === 'invoiced' ? 'Invoiced Orders' : 'Ready to Invoice'}
        metadata={[{
          label: tab === 'invoiced'
            ? 'Wholesale orders already marked invoiced in QuickBooks. Void to return one to the queue.'
            : 'Shipped wholesale orders awaiting QuickBooks invoice creation.',
        }]}
        actions={
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setPage(0); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="queue" label="Ready to Invoice" />
        <Tab value="invoiced" label="Invoiced" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {summary.map(g => (
          <Grid item xs={6} md={3} key={g.subsidiary_id}>
            <Paper sx={{ p: 2, cursor: 'pointer', bgcolor: activeSubsidiary === String(g.subsidiary_id) ? 'action.selected' : 'inherit' }}
              onClick={() => setActiveSubsidiary(String(g.subsidiary_id))}>
              <Typography variant="caption" color="textSecondary">
                {g.subsidiary_name || (g.subsidiary_id === 0 ? 'Unmapped' : `Subsidiary #${g.subsidiary_id}`)}
              </Typography>
              <Typography variant="h5" fontWeight="bold">{g.count}</Typography>
              <Typography variant="caption" display="block">{formatCurrencyDict(g.currencies)}</Typography>
            </Paper>
          </Grid>
        ))}
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, cursor: 'pointer', bgcolor: activeSubsidiary === 'all' ? 'action.selected' : 'inherit' }}
            onClick={() => setActiveSubsidiary('all')}>
            <Typography variant="caption" color="textSecondary">All subsidiaries</Typography>
            <Typography variant="h5" fontWeight="bold">{summary.reduce((acc, g) => acc + (g.count || 0), 0)}</Typography>
            <Typography variant="caption" display="block">Total awaiting invoice</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {activeSubsidiary === 'all'
                ? 'All Subsidiaries'
                : (subsidiaryNameMap[parseInt(activeSubsidiary)] || `Subsidiary #${activeSubsidiary}`)
              } — {filtered.length} order{filtered.length === 1 ? '' : 's'}
            </Typography>
            <TextField
              size="small"
              placeholder="Search order #, customer, PO, brand…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: 320 }}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Brand</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Shipped</TableCell>
                  <TableCell>Customer</TableCell>
                  {tab === 'invoiced' ? (
                    <>
                      <TableCell>QB Invoice</TableCell>
                      <TableCell>Invoice Date</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>PO #</TableCell>
                      <TableCell>Send Via</TableCell>
                    </>
                  )}
                  <TableCell align="right">Order Value</TableCell>
                  <TableCell>Subsidiary</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>No orders match.</Typography>
                  </TableCell></TableRow>
                ) : paged.map(o => {
                  const sm = o.send_method || 'qb';
                  const subId = o.subsidiary_id || 0;
                  const isPending = o.fulfillment_status === 'pending';
                  const isPartial = o.fulfillment_status === 'partially_shipped';
                  return (
                    <TableRow key={`${o.brand_id}-${o.order_id}`} hover
                      sx={isPending ? { bgcolor: 'action.hover' } : {}}>
                      <TableCell><Chip label={o.brand_name} size="small" /></TableCell>
                      <TableCell>
                        {/* Prefer CIN7-style order_name (e.g. ISER2600-30,
                            HUK2578-41) when it exists and differs from the
                            stripped order_id — that's the canonical CIN7
                            customer-named order. order_id (CIN7-SO-78040 →
                            SO-78040) drops to a small monospaced sublabel. */}
                        {(() => {
                          const oidStripped = (o.order_id || '').replace(/^CIN7-/, '');
                          const oname = o.order_name || '';
                          const showBoth = oname && oname !== oidStripped;
                          return (
                            <>
                              <strong>{showBoth ? oname : oidStripped}</strong>
                              {showBoth && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.disabled', fontFamily: 'monospace', lineHeight: 1 }}>
                                  {oidStripped}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                        {isPending && (
                          <Chip label="Not yet shipped" size="small" color="warning"
                            sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                        {isPartial && (
                          <Chip label="Partially shipped" size="small" color="warning"
                            sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>{o.event_date}</TableCell>
                      <TableCell>{o.shipped_date || '—'}</TableCell>
                      <TableCell>
                        {o.customer_id ? (
                          <RouterLink to={`/retail/customers/${encodeURIComponent(o.customer_id)}/history`}
                            style={{ color: 'inherit', textDecoration: 'underline dotted' }}>
                            {o.customer_name || o.customer_id}
                          </RouterLink>
                        ) : (o.customer_name || '—')}
                      </TableCell>
                      {tab === 'invoiced' ? (
                        <>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{o.qb_invoice_id || '—'}</Typography>
                            {/* Payment-status badge — surfaces whether the
                                operator has marked this order paid yet.
                                Green chip = settled, default = still owed. */}
                            <Chip
                              label={o.payment_status === 'paid' ? 'Paid' : (o.payment_status === 'invoiced' ? 'Awaiting payment' : (o.payment_status || 'pending'))}
                              size="small"
                              color={o.payment_status === 'paid' ? 'success' : 'default'}
                              sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                            />
                          </TableCell>
                          <TableCell>{o.qb_invoice_date || '—'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{o.po_number || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={SEND_METHOD_LABEL[sm] || sm}
                              color={SEND_METHOD_COLOR[sm] || 'default'} />
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right">{o.currency} {Number(o.grand_total ?? o.order_revenue).toFixed(2)}</TableCell>
                      <TableCell>{subId === 0 ? 'Unmapped' : (subsidiaryNameMap[subId] || `#${subId}`)}</TableCell>
                      <TableCell align="right">
                        {tab === 'invoiced' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {o.payment_status !== 'paid' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<AttachMoneyIcon />}
                                title="Mark this order as paid — flips payment_status to 'paid' and stamps amount_paid. Reflected on the retail order page."
                                onClick={() => openMarkPaid(o)}>
                                Mark Paid
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<UndoIcon />}
                              title="Void this invoice mark — returns the order to the Ready-to-Invoice queue. Void the QB invoice in QuickBooks separately."
                              onClick={() => openVoid(o)}>
                              Void Invoice
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant={isPending ? 'contained' : 'outlined'}
                            color={isPending ? 'warning' : 'primary'}
                            startIcon={<ReceiptIcon />}
                            title={isPending
                              ? 'Order has NOT shipped yet — you can still invoice (e.g. proforma/pre-paid). Ship later when ready.'
                              : 'Mark this order as invoiced in QB'}
                            onClick={() => openInvoice(o)}>
                            {isPending ? 'Invoice (unshipped)' : 'Mark Invoiced'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <Dialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Invoiced — {(invoiceTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {invoiceTarget && (
            <Box sx={{ pt: 1 }}>
              {invoiceTarget.fulfillment_status === 'pending' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>This order has NOT shipped yet.</strong> Marking it invoiced now is valid for proforma / pre-paid workflows but means the FX rate (locked at ship time) will be applied later, when you actually ship. If you intend to ship today, cancel this dialog and set the Dispatch date on the order page first.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {invoiceTarget.customer_name || invoiceTarget.customer_id} — {invoiceTarget.currency} {Number(invoiceTarget.grand_total ?? invoiceTarget.order_revenue).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth autoFocus label="QuickBooks Invoice ID" value={qbInvoiceId}
                    onChange={(e) => setQbInvoiceId(e.target.value)}
                    placeholder="e.g. 12345 or INV-001" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="QuickBooks Invoice Date" type="date" value={qbInvoiceDate}
                    onChange={(e) => setQbInvoiceDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submitInvoice} variant="contained" startIcon={<CheckCircleIcon />} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={voidDialogOpen} onClose={() => !voiding && setVoidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Void Invoice — {(voidTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {voidTarget && (() => {
            const isCin7Invoice = String(voidTarget.qb_invoice_id || '').startsWith('CIN7-INV-');
            return (
              <Box sx={{ pt: 1 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This will clear the invoice reference (<strong>{voidTarget.qb_invoice_id}</strong>{voidTarget.qb_invoice_date ? `, ${voidTarget.qb_invoice_date}` : ''}) and return the order to the Ready-to-Invoice queue.{' '}
                  {isCin7Invoice
                    ? <><strong>This is a historical CIN7 invoice — no QuickBooks action needed.</strong> The CIN7-INV-* marker came from the one-time CIN7 status sync; voiding it here just removes the marker on our side.</>
                    : <><strong>Void the matching QB invoice in QuickBooks separately</strong> — this action only updates SuperChargeMe.</>
                  }
                </Alert>
                <Typography variant="body2" color="textSecondary">
                  {voidTarget.customer_name || voidTarget.customer_id} — {voidTarget.currency} {Number(voidTarget.grand_total ?? voidTarget.order_revenue).toFixed(2)}
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialogOpen(false)} disabled={voiding}>Cancel</Button>
          <Button onClick={submitVoid} variant="contained" color="error" startIcon={<UndoIcon />} disabled={voiding}>
            {voiding ? 'Voiding…' : 'Void Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paidDialogOpen} onClose={() => !marking && setPaidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Paid — {(paidTarget?.order_id || '').replace(/^CIN7-/, '')}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {paidTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Flips <strong>payment_status</strong> to <strong>paid</strong> and stamps <strong>amount_paid</strong> with the order grand total (if not already recorded). Visible immediately on the retail order page and anywhere payment_status is displayed.
              </Alert>
              <Typography variant="body2" color="textSecondary">
                {paidTarget.customer_name || paidTarget.customer_id} — {paidTarget.currency} {Number(paidTarget.grand_total ?? paidTarget.order_revenue).toFixed(2)}
              </Typography>
              {paidTarget.qb_invoice_id && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  QB Invoice: <span style={{ fontFamily: 'monospace' }}>{paidTarget.qb_invoice_id}</span>{paidTarget.qb_invoice_date ? ` (${paidTarget.qb_invoice_date})` : ''}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialogOpen(false)} disabled={marking}>Cancel</Button>
          <Button onClick={submitMarkPaid} variant="contained" color="success" startIcon={<AttachMoneyIcon />} disabled={marking}>
            {marking ? 'Marking…' : 'Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
